import type { Messages } from './messages-en';

/**
 * French interface catalogue (D260814d).
 *
 * Typed as `Messages`, the shape of `messages-en.ts`: a missing key, a spurious
 * one, or a different parameter list is a compilation error rather than an
 * English word appearing in a French sentence.
 *
 * **Impersonal register throughout**: "Se déconnecter", "Aucun compte", never
 * "vous". The instance is installed and read by the same person, and the choice
 * between tu and vous would have to be made at every sentence, wrongly at least
 * once.
 */
export const fr: Messages = {
  /* ------------------------------------------------------------- Everywhere */

  'common.loadingLabel': 'Chargement',

  'password.show': 'Afficher le mot de passe',
  'password.hide': 'Masquer le mot de passe',

  /* ---------------------------------------------------------------- Sign in */

  'login.subtitle': 'Se connecter pour reprendre la journée.',
  'login.username': 'Identifiant',
  'login.password': 'Mot de passe',
  'login.submit': 'Se connecter',
  'login.submitting': 'Connexion…',
  'login.failed': 'Le serveur est injoignable. Réessayer.',
  'login.noAccount': 'Aucun compte n’existe encore sur cette instance.',
  'login.createAdmin': 'Créer le premier depuis un terminal sur le serveur, puis se connecter ici.',

  /* ---------------------------------------------------------------- Top bar */

  'topbar.signOut': 'Se déconnecter',
  'topbar.language': 'Langue',

  /* ------------------------------------------------------------- Navigation */

  'nav.modules': 'Modules',
  'nav.soon': 'Bientôt',

  'module.todos': 'Tâches',
  'module.reminders': 'Rappels',
  'module.notes': 'Notes',
  'module.snippets': 'Fragments',

  /* ----------------------------------------------------------------- Search */

  'search.open': 'Rechercher',
  'search.shortcut': 'Rechercher (Ctrl+K)',
  'search.placeholder': 'Chercher partout, ou #étiquette',
  'search.empty': 'Ce qui a été touché en dernier.',
  'search.nothing': 'Rien ne correspond.',
  'search.hint': '↑ ↓ pour se déplacer, Entrée pour ouvrir, Échap pour fermer.',
  'search.offline': 'Rien n’est consultable sans connexion.',
  'search.failed': 'La recherche n’a pas pu être lancée.',

  /* ------------------------------------------------------------------- Tags */

  'tags.label': 'Étiquettes',
  'tags.add': 'Ajouter une étiquette',
  'tags.remove': (name: string) => `Retirer l’étiquette « ${name} »`,
  'tags.rule': 'Minuscules, chiffres et tirets, sans espace.',
  'tags.full': (count: number) => `${count} étiquettes est le maximum sur une ligne.`,
  'tags.suggestions': 'Étiquettes existantes',
  'tags.filters': 'Filtrer par étiquette',
  'tags.all': 'Toutes',

  /* ------------------------------------------------------------------ Todos */

  'todos.add': 'Ajouter une tâche',
  'todos.addAction': 'Ajouter',
  'todos.due': 'Échéance',
  'todos.clearDue': 'Sans échéance',

  'todos.filters': 'Afficher',
  'todos.filterOpen': 'En cours',
  'todos.filterToday': 'Aujourd’hui',
  'todos.filterAll': 'Toutes',
  'todos.done': 'Terminées',

  'todos.markDone': (title: string) => `Marquer « ${title} » comme faite`,
  'todos.markOpen': (title: string) => `Marquer « ${title} » comme à faire`,
  'todos.rename': (title: string) => `Renommer « ${title} »`,
  'todos.archive': (title: string) => `Archiver « ${title} »`,
  'todos.actions': 'Actions',

  'todos.dueToday': 'Aujourd’hui',
  'todos.dueTomorrow': 'Demain',
  'todos.dueYesterday': 'Hier',

  'todos.emptyTitle': 'Rien sur la liste',
  'todos.emptyBody': 'Ce dont la journée est faite se range ici.',
  'todos.nothingHere': 'Rien sous ce filtre',
  'todos.nothingHereBody': 'Il y a des tâches sur la liste, mais aucune ici.',
  'todos.allDoneTitle': 'Tout est fait',
  'todos.allDone': (count: number) =>
    count === 1 ? 'Une tâche terminée.' : `${count} tâches terminées.`,

  'todos.errorTitle': 'La liste n’a pas pu être chargée',
  'todos.errorFallback': 'Le serveur est injoignable.',
  'todos.retry': 'Réessayer',

  'todos.offlineTitle': 'Pas de connexion',
  'todos.offlineBody':
    'La liste n’est jamais gardée sur cet appareil : il n’y a rien à afficher tant que la connexion n’est pas revenue.',

  'todos.archived': (count: number) =>
    count === 1 ? '1 tâche archivée' : `${count} tâches archivées`,
  'todos.undo': 'Annuler',
  'todos.dismiss': 'Fermer',

  /* -------------------------------------------------------------- Reminders */

  'reminders.add': 'Ajouter un rappel',
  'reminders.addAction': 'Ajouter',
  'reminders.at': 'Heure',

  'reminders.filters': 'Afficher',
  'reminders.filterNext': 'À venir',
  'reminders.filterToday': 'Aujourd’hui',
  'reminders.filterAll': 'Tous',
  'reminders.done': 'Traités',

  'reminders.atToday': (time: string) => `Aujourd’hui ${time}`,
  'reminders.atTomorrow': (time: string) => `Demain ${time}`,
  'reminders.atYesterday': (time: string) => `Hier ${time}`,
  'reminders.atDay': (day: string, time: string) => `${day} ${time}`,

  'reminders.markDone': (title: string) => `Marquer « ${title} » comme traité`,
  'reminders.markStanding': (title: string) => `Marquer « ${title} » comme à traiter`,
  'reminders.rename': (title: string) => `Renommer « ${title} »`,
  'reminders.archive': (title: string) => `Archiver « ${title} »`,

  'reminders.emptyTitle': 'Rien à venir',
  'reminders.emptyBody': 'Ce qui doit arriver à une heure précise se range ici.',
  'reminders.nothingHere': 'Rien sous ce filtre',
  'reminders.nothingHereBody': 'Il y a des rappels sur la liste, mais aucun ici.',
  'reminders.allDoneTitle': 'Tout est traité',
  'reminders.allDone': (count: number) =>
    count === 1 ? 'Un rappel traité.' : `${count} rappels traités.`,

  'reminders.errorTitle': 'Les rappels n’ont pas pu être chargés',
  'reminders.errorFallback': 'Le serveur est injoignable.',
  'reminders.retry': 'Réessayer',

  'reminders.archived': (count: number) =>
    count === 1 ? '1 rappel archivé' : `${count} rappels archivés`,
  'reminders.undo': 'Annuler',
  'reminders.dismiss': 'Fermer',

  /* ------------------------------------------------------------------ Notes */

  'notes.new': 'Nouvelle note',
  'notes.untitled': 'Sans titre',
  'notes.body': 'Note',
  'notes.placeholder': 'Markdown. La première ligne devient le titre.',
  'notes.view': 'Affichage',
  'notes.write': 'Écrire',
  'notes.preview': 'Aperçu',
  'notes.saving': 'Enregistrement…',
  'notes.saved': 'Enregistrée',
  'notes.saveFailed': 'Non enregistrée',
  'notes.backToList': 'Retour aux notes',
  'notes.missingTitle': 'Cette note n’est pas là',

  'notes.folders': 'Dossiers',
  'notes.folder': 'Dossier',
  'notes.folderName': 'Nom du dossier',
  'notes.everything': 'Tout',
  'notes.inbox': 'Boîte de réception',
  'notes.newFolder': 'Nouveau dossier',
  'notes.renameFolder': (name: string) => `Renommer « ${name} »`,
  'notes.deleteFolder': (name: string) => `Supprimer « ${name} »`,
  'notes.folderFailed': 'Le serveur est injoignable.',

  'notes.emptyTitle': 'Rien d’écrit pour l’instant',
  'notes.emptyBody': 'Ce dont il faut se souvenir sans que ce soit une tâche se range ici.',
  'notes.nothingHere': 'Rien ici',
  'notes.nothingHereBody': 'Il y a des notes, mais aucune dans ce dossier.',

  'notes.errorTitle': 'Les notes n’ont pas pu être chargées',
  'notes.errorFallback': 'Le serveur est injoignable.',
  'notes.retry': 'Réessayer',

  'notes.archive': (title: string) => `Archiver « ${title} »`,
  'notes.archived': (count: number) =>
    count === 1 ? '1 note archivée' : `${count} notes archivées`,
  'notes.undo': 'Annuler',
  'notes.dismiss': 'Fermer',

  /* --------------------------------------------------------- Quick capture */

  'notes.capture': 'Écrire une note',
  'notes.captureShortcut': 'Écrire une note (n)',
  'notes.capturePlaceholder': 'Tout ce qui mérite d’être gardé.',
  'notes.captureAction': 'Garder',
  'notes.captureHint': 'Entrée pour garder, Maj+Entrée pour une nouvelle ligne.',
  'notes.captureFailed': 'Cette note n’a pas pu être gardée.',
  'notes.waiting': (count: number) =>
    count === 1 ? '1 note en attente de connexion' : `${count} notes en attente de connexion`,

  /* ------------------------------------------------- Notifications, per device */

  'push.title': 'Notifications sur cet appareil',
  'push.on': 'Activées',
  'push.off': 'Désactivées',
  'push.enable': 'Activer',
  'push.disable': 'Désactiver',
  'push.working': 'Un instant…',
  'push.onBody': 'Les rappels sonneront tant que le navigateur tourne — sur téléphone, même fermé.',
  'push.offBody': 'Les rappels resteront affichés sur cet écran, mais rien ne sonnera.',
  'push.perDevice': 'Chaque appareil est autorisé séparément.',
  'push.deniedTitle': 'Notifications bloquées',
  'push.deniedBody':
    'Ce navigateur a reçu l’ordre de les refuser. Les réglages du site, à côté de la barre d’adresse, sont le seul retour possible.',
  'push.unsupportedTitle': 'Ce navigateur ne peut pas sonner',
  'push.unsupportedBody':
    'Sur iPhone et iPad, ajouter d’abord kanto à l’écran d’accueil : Safari n’autorise les notifications que là.',
  'push.unavailableTitle': 'Notifications indisponibles',
  'push.unavailableBody': 'Le serveur n’a fourni aucune clé : aucun abonnement n’est possible.',
  'push.dismissed':
    'Ce navigateur n’a pas posé la question. Autoriser les notifications pour ce site depuis l’icône dans la barre d’adresse, puis appuyer de nouveau sur Activer.',
  'push.failed': 'Cela n’a pas fonctionné. Réessayer.',
};
